import { RiskAssessmentStatusTitle, RiskLevels, RiskScoreType } from "./enums";
import theme from "../../../../utils/theme";

/**
 * Get risk level based on score
 * @param {number} score - The risk score
 * @returns {string} The risk level ('Low', 'Medium', or 'High')
 */
export const getRiskLevel = (score) => {
  if (score >= 19) return RiskLevels.HIGH;
  if (score >= 10) return RiskLevels.MEDIUM;
  return RiskLevels.LOW;
};

/**
 * Get risk statistics from an array of risks
 * @param {Array} risks - Array of risk objects
 * @returns {Object} Object containing total, low, medium, and high risk counts
 */
export const getRiskStats = (risks = []) => {
  const total = risks.length;

  const low = risks.filter((risk) => {
    const score =
      risk.calculated_residual_risk_score || risk.residual_risk_score;
    return score >= 1 && score <= 9;
  }).length;

  const medium = risks.filter((risk) => {
    const score =
      risk.calculated_residual_risk_score || risk.residual_risk_score;
    return score >= 10 && score <= 18;
  }).length;

  const high = risks.filter((risk) => {
    const score =
      risk.calculated_residual_risk_score || risk.residual_risk_score;
    return score >= 19 && score <= 25;
  }).length;

  return { total, low, medium, high };
};

/**
 * Get background color for risk score visualization
 * @param {number} score - The risk score
 * @returns {string} The background color from theme
 */
export const getRiskColor = (score) => {
  if (score >= 19) return theme.customColors.status.error.bg;
  if (score >= 10) return theme.customColors.status.warning.bg;
  return theme.customColors.status.success.bg;
};

/**
 * Get text color for risk score visualization
 * @param {number} score - The risk score
 * @returns {string} The text color from theme
 */
export const getRiskTextColor = (score) => {
  if (score >= 19) return theme.customColors.status.error.text;
  if (score >= 10) return theme.customColors.status.warning.text;
  return theme.customColors.status.success.text;
};

/**
 * Get status chip configuration for snapshot status
 * @param {string} status - The status string
 * @returns {Object} Object containing color, icon, and label for the status chip
 */
export const getAssessmentStatusChip = (status) => {
  switch (status) {
    case RiskAssessmentStatusTitle.DRAFT:
      return {
        color: "warning",
        icon: "ScheduleIcon",
        label: RiskAssessmentStatusTitle.DRAFT,
      };
    case RiskAssessmentStatusTitle.COMPLETED:
      return {
        color: "success",
        icon: "CheckCircleIcon",
        label: RiskAssessmentStatusTitle.COMPLETED,
      };
    case RiskAssessmentStatusTitle.DISCARDED:
      return {
        color: "default",
        icon: null,
        label: status,
      };
  }
};

/**
 * Validate override values
 * @param {number} newScore - The new calculated score
 * @param {number} inherentScore - The inherent risk score
 * @param {string} overrideType - The type of override ('inherent' or 'residual')
 * @returns {Object} Validation result with isValid and error message
 */
export const validateOverride = (
  newScore,
  inherentScore,
  overrideType = RiskScoreType.RESIDUAL
) => {
  if (overrideType === RiskScoreType.RESIDUAL && newScore > inherentScore) {
    return {
      isValid: false,
      error: `Residual risk score (${newScore}) cannot be greater than inherent risk score (${inherentScore})`,
    };
  }

  if (newScore < 1 || newScore > 25) {
    return {
      isValid: false,
      error: "Risk score must be between 1 and 25",
    };
  }

  return {
    isValid: true,
    error: "",
  };
};

/**
 * Calculates the control effectiveness percentage for a risk object.
 * @param {Object} risk - The risk object containing controls and checks.
 * @returns {number} - The effectiveness percentage (0-100).
 */
export function getChecksEffectivenessForRisk(risk) {
  if (!risk?.total_checks || risk?.total_checks === 0) return 0;

  return Math.round((risk?.passed_checks / risk?.total_checks) * 100);
}

export const IMPACT_LEVELS = [
  {
    value: 1,
    label: "1 - Very Low",
    description: "Minimal impact on operations",
  },
  {
    value: 2,
    label: "2 - Low",
    description: "Minor impact on operations",
  },
  {
    value: 3,
    label: "3 - Medium",
    description: "Moderate impact on operations",
  },
  {
    value: 4,
    label: "4 - High",
    description: "Significant impact on operations",
  },
  {
    value: 5,
    label: "5 - Very High",
    description: "Critical impact on operations",
  },
];

export const LIKELIHOOD_LEVELS = [
  {
    value: 1,
    label: "1 - Very Low",
    description: "Rarely occurs",
  },
  {
    value: 2,
    label: "2 - Low",
    description: "Occasionally occurs",
  },
  {
    value: 3,
    label: "3 - Medium",
    description: "Sometimes occurs",
  },
  {
    value: 4,
    label: "4 - High",
    description: "Frequently occurs",
  },
  {
    value: 5,
    label: "5 - Very High",
    description: "Almost certain to occur",
  },
];
